import React, { useEffect, useState } from 'react';
import { css, useTheme } from '@emotion/react';
import { type InlineEditorHints } from '@bbk/ui/inline-editing';
import {
  type RestModalEmailSendModal,
  type RestModalForwards,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { cs } from '@bbkAdminUtils/css-utils';
import { FunnelButton, FunnelTextArea } from '@bbkFunnel/components/v2';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import {
  ModalDesktopInnerSpacer,
  OfferTitleMobile,
  TitleTheme,
  useMediaQueries,
} from '../sections';
import ActionButtonIcons from '../sections/action-button-helpers';

type Props = {
  modalInfo: RestModalEmailSendModal;
  close: () => void;
  handleAction: (
    forwards: RestModalForwards,
    { message }: { message: string }
  ) => void;
};

const RenderEmailCompose: React.FC<Props> = ({
  modalInfo: {
    id,
    requires: {
      forwards,
      forwards: { icon, text } = {},
      forwards: {
        sync: { message },
      },
      info: { title, placeholder },
    },
  },
  close,
  handleAction,
}) => {
  const { mobileView, tabletView } = useMediaQueries();
  const [messageContent, setMessageContent] = useState(message || '');
  const theme = useTheme() as EmotionTheme;
  const equalButtonSize = theme.complianceEqualButtonSize;

  useEffect(() => {
    setMessageContent(message || '');
  }, [message]);

  const funnelLabelStyle = {
    display: 'block',
    paddingLeft: '5px',
    paddingRight: '5px',
  };
  if (icon) {
    funnelLabelStyle.display = 'inline-block';
  }

  const buttonDisabled =
    messageContent === '' ||
    messageContent === null ||
    messageContent === undefined;

  return (
    <ModalDesktopInnerSpacer
      css={css`
        ${mobileView && 'padding: 12px 1rem; flex: 1 1 auto;'}
        ${tabletView && 'width: 450px;'}
      `}
      className={cs('email-compose', 'display-grid', 'inner-spacer')}
    >
      <OfferTitleMobile
        title={
          <TitleTheme
            title={title}
            css={{ marginBottom: 0 }}
            data-part="title"
          />
        }
        close={close}
      />
      <div css={{ marginBottom: '1rem' }} data-part="description">
        <FunnelTextArea
          name="feedback"
          value={messageContent}
          rows={6}
          placeholder={placeholder ?? 'Start typing...'}
          onChange={(e) => setMessageContent(e.target.value)}
          onBlur={(e) => setMessageContent(e.target.value)}
          maxLength={400}
          modalTextColor
        />
      </div>
      {/* Added padding to make total 27px in mobile for equal size buttons */}
      <div
        className={cs({
          'tw-px-[11px]': equalButtonSize && mobileView,
          'tw-flex tw-justify-center': equalButtonSize && !mobileView,
        })}
      >
        <FunnelButton
          data-part="primary-action"
          disabled={buttonDisabled}
          specificStyles={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
          onClick={() => handleAction(forwards, { message: messageContent })}
        >
          {icon && <ActionButtonIcons icon={icon} />}
          <span style={funnelLabelStyle}>{text}</span>
        </FunnelButton>
      </div>
    </ModalDesktopInnerSpacer>
  );
};

export const EmailCompose = Object.assign(RenderEmailCompose, {
  editorHints: {
    title: {
      path: ['requires', 'info', 'title'],
      control: 'text',
      selector: '[data-part="title"]',
      placement: 'right',
    },
    description: {
      path: ['requires'],
      control: 'group',
      selector: '[data-part="description"]',
      placement: 'right',
      fields: [
        {
          path: ['info', 'placeholder'],
          control: 'text',
          label: 'Placeholder',
        },
        {
          path: ['forwards', 'sync', 'message'],
          control: 'text',
          label: 'Message',
          options: {
            multiline: true,
          },
        },
      ],
    },
    action: {
      path: ['requires', 'forwards'],
      control: 'ctaAppearance',
      selector: '[data-part="primary-action"]',
      placement: 'top-end',
      label: 'Action',
      options: {
        message: false,
      },
    },
  } satisfies InlineEditorHints,
});
